import React, { type JSX } from "react";
import {
  DocumentIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

import { type DownloadProgress } from "./documentPreviewTypes";

const BYTES_PER_MB = 1024 * 1024;

/** Indeterminate (null percentage) progress pulses at a nominal 30% width. */
export function ProgressBar({
  percentage,
  caption,
}: {
  percentage: number | null;
  caption: string;
}): JSX.Element {
  return (
    <div className="w-64 mx-auto">
      <div className="bg-gray-200 rounded-full h-2.5 overflow-hidden">
        <div
          className={`bg-cvc-blue h-2.5 rounded-full transition-all duration-300 ease-out ${
            percentage === null ? "animate-pulse" : ""
          }`}
          style={{ width: percentage !== null ? `${percentage}%` : "30%" }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-2">{caption}</p>
    </div>
  );
}

function DownloadedSoFar({ progress }: { progress: DownloadProgress }): JSX.Element {
  const loadedMb = (progress.loaded / BYTES_PER_MB).toFixed(1);

  return (
    <>
      <div className="mb-4">
        <DocumentIcon className="h-12 w-12 mx-auto text-cvc-blue mb-2" />
        <p className="text-sm font-medium text-gray-900 mb-1">Downloading from SharePoint</p>
        <p className="text-xs text-gray-500 mb-4">
          {progress.total
            ? `${loadedMb} MB of ${(progress.total / BYTES_PER_MB).toFixed(1)} MB`
            : `${loadedMb} MB downloaded`}
        </p>
      </div>
      <ProgressBar
        percentage={progress.percentage}
        caption={
          progress.percentage !== null ? `${progress.percentage}% complete` : "Downloading..."
        }
      />
    </>
  );
}

export function PreviewLoading({ progress }: { progress: DownloadProgress }): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        {progress.isDownloading ? (
          <DownloadedSoFar progress={progress} />
        ) : (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cvc-blue mx-auto mb-4"></div>
            <p className="text-sm text-gray-600">Loading document preview...</p>
          </>
        )}
      </div>
    </div>
  );
}

export function NoDocumentSelected(): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full text-gray-500">
      <div className="text-center">
        <DocumentIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
        <p>No document selected</p>
      </div>
    </div>
  );
}

export function PreviewNotAvailable({ filename }: { filename: string }): JSX.Element {
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4 text-yellow-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Not Available</h3>
          <p className="text-sm text-gray-600 mb-4 max-w-md">
            Preview is only available for PDF and Markdown files. This document type is not
            supported for preview.
          </p>
          <p className="text-xs text-gray-500">File: {filename}</p>
        </div>
      </div>
    </div>
  );
}

export function NoPreviewAvailable(): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <EyeIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-sm text-gray-600">No preview available</p>
      </div>
    </div>
  );
}

export function UnknownDocumentType(): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <DocumentIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-sm text-gray-600">Unknown document type</p>
      </div>
    </div>
  );
}

/** Graph could not convert this document, so only the raw file is on offer. */
export function DownloadOnlyPrompt({
  filename,
  downloading,
  onDownload,
}: {
  filename: string;
  downloading: boolean;
  onDownload: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full p-8">
      <div className="text-center max-w-lg">
        <DocumentIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-sm text-gray-600 mb-6">
          This document contains elements which cannot be viewed in the browser.
        </p>
        <button
          onClick={onDownload}
          disabled={downloading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
          {downloading ? "Downloading..." : `Download ${filename}`}
        </button>
      </div>
    </div>
  );
}

export function PlainTextView({ text }: { text: string }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
          {text}
        </pre>
      </div>
    </div>
  );
}
